import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { wishlistAPI } from '../services/api';

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch wishlist IDs on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlistIds();
    } else {
      setWishlistIds(new Set());
      setWishlistCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch wishlist product IDs
  const fetchWishlistIds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await wishlistAPI.getIds();
      if (response.success) {
        setWishlistIds(new Set(response.productIds));
        setWishlistCount(response.count);
      }
    } catch (error) {
      // Silently handle errors - wishlist table might not exist yet
      // This is expected for fresh installations
      setWishlistIds(new Set());
      setWishlistCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if product is in wishlist
  const isInWishlist = useCallback((productId) => {
    return wishlistIds.has(productId);
  }, [wishlistIds]);

  // Toggle product in wishlist
  const toggleWishlist = useCallback(async (productId) => {
    if (!isAuthenticated) {
      return { success: false, message: 'Please login to add items to wishlist' };
    }

    try {
      const response = await wishlistAPI.toggle(productId);
      
      if (response.success) {
        setWishlistIds((prev) => {
          const newSet = new Set(prev);
          if (response.action === 'added') {
            newSet.add(productId);
            setWishlistCount((c) => c + 1);
          } else {
            newSet.delete(productId);
            setWishlistCount((c) => Math.max(0, c - 1));
          }
          return newSet;
        });
      }
      
      return response;
    } catch (error) {
      console.error('Failed to toggle wishlist:', error);
      // Provide user-friendly error message
      const message = error.message?.includes('database') || error.message?.includes('table')
        ? 'Wishlist feature is being set up. Please try again later.'
        : error.message || 'Failed to update wishlist';
      return { success: false, message };
    }
  }, [isAuthenticated]);

  // Add to wishlist
  const addToWishlist = useCallback(async (productId, options = {}) => {
    if (!isAuthenticated) {
      return { success: false, message: 'Please login to add items to wishlist' };
    }

    try {
      const response = await wishlistAPI.add(productId, options);
      
      if (response.success) {
        setWishlistIds((prev) => new Set([...prev, productId]));
        setWishlistCount((c) => c + 1);
      }
      
      return response;
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      return { success: false, message: error.message };
    }
  }, [isAuthenticated]);

  // Remove from wishlist
  const removeFromWishlist = useCallback(async (productId) => {
    try {
      const response = await wishlistAPI.removeByProduct(productId);
      
      if (response.success) {
        setWishlistIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        setWishlistCount((c) => Math.max(0, c - 1));
      }
      
      return response;
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      return { success: false, message: error.message };
    }
  }, []);

  // Clear wishlist
  const clearWishlist = useCallback(async () => {
    try {
      const response = await wishlistAPI.clear();
      
      if (response.success) {
        setWishlistIds(new Set());
        setWishlistCount(0);
      }
      
      return response;
    } catch (error) {
      console.error('Failed to clear wishlist:', error);
      return { success: false, message: error.message };
    }
  }, []);

  const value = {
    wishlistIds,
    wishlistCount,
    loading,
    isInWishlist,
    toggleWishlist,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    refreshWishlist: fetchWishlistIds,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export default WishlistContext;
